import { FormControl, FormHelperText } from "@mui/material";
import type { FC } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import type { ICustomField } from "@ryde/shared";

import { GroupTitleTemplate } from "../group_title.template";
import { CatchARideAvailabilityItemAdd } from "./catch-a-ride-availability-item.add";
import { CatchARideAvailabilityRow } from "./catch-a-ride-availability-row";
import { CatchARideAvailabilityStyled } from "./catch-a-ride-availability.styled";
import { catchARideAvailabilitySerialized } from "./catch-a-ride-availability.utils";
import { useCatchARideAvailabilityTemplate } from "./use-catch-a-ride-availability-template";

export const CatchARideAvailabilityTemplate: FC<ICustomField> = ({
  name,
  formLabel,
  formAttributes,
}) => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext();
  const watchedValue = useWatch({ control, name });
  const {
    availabilityList,
    removeHandler,
    isInnerItemEditing,
    handleEditStart,
    handleEditEnd,
  } = useCatchARideAvailabilityTemplate({ name, watchedValue, setValue });
  const { disabled, descriptionTranslationKey } = formAttributes || {};

  return (
    <CatchARideAvailabilityStyled>
      <Controller
        name={name}
        control={control}
        render={({ fieldState: { error } }) => (
          <FormControl
            error={!!error}
            data-e2e-template="catch-a-ride-availability-template"
          >
            <GroupTitleTemplate
              formLabel={formLabel}
              name={name}
              formAttributes={formAttributes}
            />
            {descriptionTranslationKey && (
              <p className="catch-a-ride-availability__description">
                {t(descriptionTranslationKey)}
              </p>
            )}

            <div
              className="availability__container"
              data-inner-editing={isInnerItemEditing ? "true" : undefined}
            >
              {availabilityList.map((availabilityItem, index) => (
                <CatchARideAvailabilityRow
                  key={`${index}_${catchARideAvailabilitySerialized(availabilityItem)}`}
                  fieldName={name}
                  index={index}
                  removeHandler={removeHandler}
                  parentControl={control}
                  parentSetValue={setValue}
                  disabledEdit={disabled}
                  disabledRemove={disabled}
                  onEditStart={handleEditStart}
                  onEditEnd={handleEditEnd}
                />
              ))}
              {!disabled && (
                <CatchARideAvailabilityItemAdd
                  fieldName={name}
                  index={availabilityList.length}
                  setValueOuter={setValue}
                  formAttributes={formAttributes}
                  onEditStart={handleEditStart}
                  onEditEnd={handleEditEnd}
                />
              )}
            </div>
            {error && <FormHelperText>{error.message}</FormHelperText>}
          </FormControl>
        )}
      />
    </CatchARideAvailabilityStyled>
  );
};
